import {
    Alert, Box, Button, Chip, CircularProgress, Divider,
    LinearProgress, Snackbar, Table, TableBody, TableCell,
    TableHead, TableRow, Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import ApiServices from '../../services/ApiServices';
import { isAdmin } from '../Constants';

const TERMINAL_STAGES = ['OTA_COMPLETE', 'OTA_FAILED', 'ABORT_ACCEPTED', 'ABORT_REJECTED', 'REQUEST_REJECTED', 'FORCE_STOPPED'];
const POLL_INTERVAL_MS = 5000;

const stageColor = (stage) => {
    if (!stage) return 'default';
    if (stage === 'OTA_COMPLETE' || stage === 'FLASH_SUCCESS') return 'success';
    if (['OTA_FAILED', 'REQUEST_REJECTED', 'DOWNLOAD_FAILED', 'FLASH_FAILED', 'ABORT_REJECTED'].includes(stage)) return 'error';
    if (['ABORT_ACCEPTED', 'FORCE_STOPPED'].includes(stage)) return 'warning';
    if (stage === 'PENDING') return 'default';
    return 'info';
};

const BINARY_COLUMNS = [
    { field: 'binaryType', headerName: 'Component', flex: 0.8, sortable: false },
    { field: 'versionFrom', headerName: 'From', flex: 0.7, sortable: false,
      renderCell: (p) => p.value ?? '—' },
    { field: 'versionTo', headerName: 'To', flex: 0.7, sortable: false },
    { field: 'stage', headerName: 'Stage', flex: 0.9, sortable: false,
      renderCell: (p) => <Chip label={p.value ?? '—'} color={stageColor(p.value)} size="small" /> },
    { field: 'progressPercent', headerName: 'Progress', flex: 0.9, sortable: false,
      renderCell: (p) => {
          if (p.row.stage !== 'DOWNLOAD_IN_PROGRESS') return '—';
          const pct = p.value ?? 0;
          return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <LinearProgress variant="determinate" value={pct} sx={{ flex: 1 }} />
                  <Typography variant="caption">{pct}%</Typography>
              </Box>
          );
      }
    },
    { field: 'infoMsg', headerName: 'Info', flex: 1.4, sortable: false,
      renderCell: (p) => (
          <Typography variant="caption" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
              {p.value ?? '—'}
          </Typography>
      )
    },
    { field: 'errorCode', headerName: 'Error', flex: 0.5, sortable: false,
      renderCell: (p) => p.value != null ? p.value : '—' },
    { field: 'updatedAt', headerName: 'Updated', flex: 1, sortable: false,
      renderCell: (p) => p.value ? moment(p.value).format('HH:mm:ss') : '—' },
];

function GwOtaProgress({ gatewayId }) {
    const [session, setSession] = useState(null);
    const [binaries, setBinaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [aborting, setAborting] = useState(false);
    const [forceStopping, setForceStopping] = useState(false);
    const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });
    const intervalRef = useRef(null);

    const showSnack = (message, severity = 'error') =>
        setSnack({ open: true, severity, message });

    const fetchActive = useCallback(() => {
        ApiServices.getActiveGwSelfOtaSession(gatewayId)
            .then(response => {
                if (response.status === 204 || !response.data) {
                    setSession(null);
                    setBinaries([]);
                    clearInterval(intervalRef.current);
                } else {
                    const { session: s, binaries: b } = response.data;
                    setSession(s);
                    setBinaries(b ?? []);
                    if (TERMINAL_STAGES.includes(s?.stage)) {
                        clearInterval(intervalRef.current);
                    }
                }
            })
            .catch(err => showSnack(err.response?.data ?? err.message))
            .finally(() => setLoading(false));
    }, [gatewayId]);

    useEffect(() => {
        fetchActive();
        intervalRef.current = setInterval(fetchActive, POLL_INTERVAL_MS);
        return () => clearInterval(intervalRef.current);
    }, [fetchActive]);

    const handleAbort = () => {
        if (!session) return;
        setAborting(true);
        ApiServices.abortGwSelfOta(gatewayId, session.otaId)
            .then(() => { showSnack('Abort request sent.', 'success'); fetchActive(); })
            .catch(err => showSnack(err.response?.data ?? err.message))
            .finally(() => setAborting(false));
    };

    const handleForceStop = () => {
        if (!session) return;
        setForceStopping(true);
        ApiServices.forceStopGwSelfOta(gatewayId, session.otaId)
            .then(() => { showSnack('Session force-stopped.', 'success'); fetchActive(); })
            .catch(err => showSnack(err.response?.data ?? err.message))
            .finally(() => setForceStopping(false));
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress /></Box>;
    }

    if (!session) {
        return (
            <Box sx={{ mt: 2 }}>
                <Typography color="text.secondary">No active GW self-OTA session.</Typography>
            </Box>
        );
    }

    const isTerminal = TERMINAL_STAGES.includes(session.stage);
    const binaryColumns = isTerminal
        ? BINARY_COLUMNS.filter(c => c.field !== 'progressPercent')
        : BINARY_COLUMNS;

    return (
        <Box>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={4000}
                open={snack.open}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
            >
                <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>{snack.message}</Alert>
            </Snackbar>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                    <Typography variant="body2"><b>OTA ID:</b> {session.otaId}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <b>Stage:</b>{' '}
                        <Chip label={session.stage ?? '—'} color={stageColor(session.stage)} size="small" />
                    </Typography>
                    <Typography variant="body2"><b>Started by:</b> {session.updatedBy ?? '—'}</Typography>
                    <Typography variant="body2"><b>Force Update:</b> {session.forceUpdate ? 'Yes' : 'No'}</Typography>
                    <Typography variant="body2"><b>Retries:</b> {session.retryCount ?? 0}</Typography>
                    <Typography variant="body2"><b>Started:</b>{' '}
                        {session.createdAt ? moment(session.createdAt).format('DD-MM-YYYY HH:mm:ss') : '—'}
                    </Typography>
                </Box>
                {!isTerminal && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                        <Button variant="outlined" color="error" size="small" onClick={handleAbort} disabled={aborting || forceStopping}>
                            {aborting ? 'Aborting…' : 'Abort OTA'}
                        </Button>
                        {isAdmin() && (
                            <Button variant="contained" color="error" size="small" onClick={handleForceStop} disabled={aborting || forceStopping}>
                                {forceStopping ? 'Stopping…' : 'Force Stop'}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Component Status</Typography>
            <DataGrid
                rows={binaries}
                columns={binaryColumns}
                getRowId={(row) => row.id}
                autoHeight
                hideFooter
                disableColumnMenu
                disableSelectionOnClick
                getRowHeight={() => 'auto'}
                getEstimatedRowHeight={() => 52}
                sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
                slots={{
                    noRowsOverlay: () => (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography color="text.secondary">Waiting for status…</Typography>
                        </Box>
                    )
                }}
            />
            {!isTerminal && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Auto-refreshing every {POLL_INTERVAL_MS / 1000}s…
                </Typography>
            )}

            {(session.stageHistory ?? []).length > 0 && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Stage History</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Stage</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Timestamp</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(session.stageHistory ?? []).map((entry, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>
                                        <Chip label={entry.stage} color={stageColor(entry.stage)} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption">
                                            {entry.ts ? moment(entry.ts).format('DD-MM-YYYY HH:mm:ss') : '—'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
        </Box>
    );
}

export default GwOtaProgress;